import type { CitationSpan, Constituent, CslRef, LinkedItem, LinkedSpan, ReferenceIndex, Resolution } from './types';
import { findCitationSpans } from './find-spans';
import { indexReferences } from './index-references';
import { resolveConstituent } from './resolve';
import { buildOrdinalIndex, findNumericSpans, resolveNumericSpan } from './numeric';
import type { NumericVariant, OrdinalIndex } from './numeric';

/** How the text cites: author-year (the default), or one numeric shape. */
export const LINK_STYLES = ['author-year', 'numeric-bracket', 'numeric-paren', 'numeric-superscript'] as const;
export type LinkStyle = (typeof LINK_STYLES)[number];

export interface LinkOptions {
  style?: LinkStyle;
  /** The numbered bibliography for a numeric style. Without it the references' own
   *  bibliography lines are read for literal ordinals (never list position). */
  ordinals?: OrdinalIndex;
}

function isReferenceIndex(x: unknown): x is ReferenceIndex {
  return !Array.isArray(x) && x !== null && typeof x === 'object' && (x as ReferenceIndex).refs instanceof Map;
}

function isOrdinalIndex(x: unknown): x is OrdinalIndex {
  return !Array.isArray(x) && x !== null && typeof x === 'object' && (x as OrdinalIndex).byOrdinal instanceof Map;
}

function numericVariantOf(style: LinkStyle): NumericVariant | null {
  switch (style) {
    case 'numeric-bracket':
      return 'bracket';
    case 'numeric-paren':
      return 'paren';
    case 'numeric-superscript':
      return 'superscript';
    default:
      return null;
  }
}

/**
 * Apply a resolver trim to the constituent it was made on: the names become the surviving
 * names; when the dropped text lies inside the constituent, its start moves past it and, in a
 * parenthetical, the dropped text joins the prefix so a whole-parenthetical replacement keeps it.
 */
function applyTrim(text: string, span: CitationSpan, c: Constituent, r: Resolution): Constituent {
  if (r.kind === 'unresolved' || r.namesFrom === undefined) return c;
  const dropped = r.trimmedPrefix ?? '';
  const start = c.start + dropped.length;
  const first = c.names[r.namesFrom].slice(Math.max(0, start - c.nameOffsets[r.namesFrom]));
  const names = [first, ...c.names.slice(r.namesFrom + 1)];
  const nameOffsets = [Math.max(start, c.nameOffsets[r.namesFrom]), ...c.nameOffsets.slice(r.namesFrom + 1)];
  const out: Constituent = { ...c, start, text: text.slice(start, c.end), names, nameOffsets, arity: names.length };
  if (span.kind === 'parenthetical' && dropped.trim()) {
    out.prefix = [c.prefix, dropped.trim()].filter((p): p is string => Boolean(p)).join(' ');
  }
  return out;
}

/**
 * Find every citation span in `text` and resolve each cited work.
 *
 * A span is `linked` only when every constituent resolved; one unresolved or ambiguous
 * constituent makes the whole span `partial`, and a caller must then link nothing in it —
 * the per-constituent resolutions are there so a person can see which work was the problem.
 *
 * When the resolver linked a constituent only after trimming prose off its names, the
 * constituent is rewritten to what was linked: a narrative span's start moves to the surviving
 * name; a parenthetical constituent keeps the dropped words in its `prefix`.
 *
 * With a numeric `style` the text is read for `[1]`, `(1)` or `¹` tokens instead and each
 * ordinal is resolved against `opts.ordinals` (or an `OrdinalIndex` passed as `references`, or
 * one built from the references' own bibliography lines).
 *
 * Footnotes: a footnote body is text like any other — author-year citations inside it link as
 * usual. Reading a footnote MARKER as a citation of the note's content is a document-model
 * concern and out of scope here.
 */
export function linkText(
  text: string,
  references: ReferenceIndex | OrdinalIndex | CslRef[],
  opts: LinkOptions = {},
): LinkedSpan[] {
  const variant = numericVariantOf(opts.style ?? 'author-year');
  if (variant) {
    const ordinals =
      opts.ordinals ??
      (isOrdinalIndex(references)
        ? references
        : buildOrdinalIndex(
            (isReferenceIndex(references) ? [] : references).map((r) => ({
              id: r.id === undefined || r.id === null ? '' : String(r.id),
              text: typeof r.rawReference === 'string' ? r.rawReference : typeof r.sourceText === 'string' ? r.sourceText : undefined,
            })),
          ));
    return findNumericSpans(text, variant).map((span) => resolveNumericSpan(span, ordinals));
  }
  if (isOrdinalIndex(references)) return [];
  const index = isReferenceIndex(references) ? references : indexReferences(references);
  return findCitationSpans(text).map((found) => {
    const items: LinkedItem[] = found.constituents.map((c) => {
      const resolution = resolveConstituent(c, index, text);
      return { constituent: applyTrim(text, found, c, resolution), resolution };
    });
    const kind = items.every((i) => i.resolution.kind === 'resolved') ? 'linked' : 'partial';
    let span = found;
    if (found.kind === 'narrative' && items.length > 0 && items[0].constituent.start > found.start) {
      const start = items[0].constituent.start;
      span = { ...found, start, text: text.slice(start, found.end), constituents: items.map((i) => i.constituent) };
    } else {
      span = { ...found, constituents: items.map((i) => i.constituent) };
    }
    return { span, kind, items };
  });
}
